import React, { Fragment } from "react";
import {
  buildQuoteEstimateGroups,
  computeQuoteEstimateTotals,
} from "./quoteEstimatePrintHelpers";

// Order-facing copy of QuoteEstimatePrint: SAME visual layout as the estimate
// print, but with order wording ("Order Prepared For", "Order for…", "Order #")
// and no 30-day validity line. The grouped category table / Base Bid / Total /
// footer are reused verbatim so the order PDF matches the quote print's look.
//
// showCostColumns toggles the detail level between the two order PDFs:
//   • false → ClientPDF: clean estimate layout (Line Item + Line Total).
//   • true  → OrderPDF (admin): also shows QTY + Price columns.
// Inline styles so the layout renders identically through html2pdf/html2canvas
// without depending on app CSS.
const S = {
  wrap: { fontFamily: "Arial, Helvetica, sans-serif", color: "#222", padding: "8px" },
  header: { textAlign: "center", margin: "0 0 12px" },
  logo: { maxHeight: "90px", maxWidth: "260px", objectFit: "contain", margin: "0 auto 4px", display: "block" },
  preparedFor: { fontSize: "22px", fontWeight: 700, margin: "4px 0 0" },
  property: { fontSize: "16px", fontWeight: 600, margin: "2px 0 0" },
  planImage: { maxWidth: "260px", maxHeight: "220px", objectFit: "contain", border: "1px solid #ccc", display: "block", margin: "0 auto 4px" },
  footer: { marginTop: "16px", fontSize: "12px", lineHeight: 1.5 },
  title: { textAlign: "center", fontSize: "20px", fontWeight: 700, margin: "0 0 8px" },
  // tableLayout: fixed + an explicit colgroup keeps the columns aligned in the
  // html2pdf/html2canvas render (auto layout distributes column widths
  // inconsistently on the canvas and shifts the headers off their columns).
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed" },
  thLeft: { textAlign: "left", borderBottom: "1px solid #ccc", padding: "8px", fontWeight: 600 },
  thRight: { textAlign: "right", borderBottom: "1px solid #ccc", padding: "8px", fontWeight: 600 },
  catHead: { background: "#efefef", fontWeight: 700, fontSize: "15px", padding: "8px", borderTop: "1px solid #ddd", textAlign: "left" },
  tdLeft: { textAlign: "left", padding: "8px", borderBottom: "1px solid #eee", wordBreak: "break-word" },
  tdRight: { textAlign: "right", padding: "8px", borderBottom: "1px solid #eee" },
  baseBidLabel: { background: "#fce3c0", fontWeight: 700, padding: "8px", textAlign: "left" },
  baseBidValue: { background: "#fce3c0", fontWeight: 700, padding: "8px", textAlign: "right" },
  totalLabel: { background: "#dbe9f5", fontWeight: 700, padding: "8px", textAlign: "right" },
  totalValue: { background: "#dbe9f5", fontWeight: 700, padding: "8px", textAlign: "right" },
  detailsTable: { width: "420px", maxWidth: "70%", borderCollapse: "collapse", fontSize: "13px", margin: "0 auto 8px" },
  detailLabel: { textAlign: "left", padding: "4px 8px", fontWeight: 700, width: "40%", verticalAlign: "top", background: "#f7f7f7", border: "1px solid #e5e5e5" },
  detailValue: { textAlign: "left", padding: "4px 8px", border: "1px solid #e5e5e5", verticalAlign: "top" },
  instrWrap: { width: "420px", maxWidth: "70%", margin: "0 auto 8px", border: "1px solid #e5e5e5" },
  instrHead: { background: "#f7f7f7", fontWeight: 700, fontSize: "13px", padding: "5px 8px", borderBottom: "1px solid #e5e5e5" },
  instrBody: { padding: "6px 8px", fontSize: "13px", whiteSpace: "pre-wrap", lineHeight: 1.4 },
};

const OrderEstimatePrint = ({
  materialListData,
  laborData,
  unitName,
  unitTypeName,
  orderNumber,
  purchaseOrder,
  orderDate,
  customerInstructions,
  storeLogo,
  propertyName,
  planImage,
  handleFormat,
  // Admin OrderPDF shows QTY + Price columns; the client PDF stays on the clean
  // Line Item + Line Total layout.
  showCostColumns = false,
  // Image ids are parametrized so the two order nodes (admin + client) rendered
  // alongside each other do not collide on duplicate element ids in the live DOM.
  logoId = "order-store-logo",
  planImageId = "order-floorplan",
}) => {
  const toMoney = (n) =>
    (parseFloat(n) || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  // Reuse OrderConfirmation's handleFormat for real values (comma + 2 decimals).
  // It returns the number 0 for falsy input, so normalize that to "0.00" — the
  // estimate shows "$0.00" for zero line totals, never "$0".
  const fmt = (n) => {
    const out = typeof handleFormat === "function" ? handleFormat(n) : toMoney(n);
    return typeof out === "number" ? toMoney(out) : out;
  };

  const groups = buildQuoteEstimateGroups({ materialListData, laborData });
  const { baseBid, total, lastPrimaryName } = computeQuoteEstimateTotals(groups);
  // Column span helpers keep the category heading / Base Bid / Total rows aligned
  // whether or not the admin cost columns are present.
  const totalCols = showCostColumns ? 4 : 2;
  const labelSpan = showCostColumns ? 3 : 1;
  return (
    <div style={S.wrap}>
      <div style={S.header}>
        {storeLogo ? <img src={storeLogo} id={logoId} alt="Store Logo" style={S.logo} /> : null}
        <h1 style={S.preparedFor}>Order Prepared For</h1>
        {propertyName ? <h3 style={S.property}>{propertyName}</h3> : null}
        {planImage ? <img src={planImage} id={planImageId} alt="Floor Plan" style={S.planImage} /> : null}
      </div>
      <h3 style={S.title}>
        Order for {unitName} ({unitTypeName})
      </h3>
      {(() => {
        const details = [
          ["Order #", orderNumber],
          ["Unit #", unitName],
          ["Unit Type", unitTypeName],
          ["PO #", purchaseOrder],
          ["Date", orderDate],
        ].filter(([, v]) => v != null && String(v).trim() !== "");
        if (details.length === 0) return null;
        return (
          <table style={S.detailsTable}>
            <tbody>
              {details.map(([label, value]) => (
                <tr key={label}>
                  <td style={S.detailLabel}>{label}</td>
                  <td style={S.detailValue}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      })()}
      {customerInstructions != null && String(customerInstructions).trim() !== "" && (
        <div style={S.instrWrap}>
          <div style={S.instrHead}>Customer Instructions</div>
          <div style={S.instrBody}>{customerInstructions}</div>
        </div>
      )}
      <table style={S.table}>
        {/* Client PDF shows only the line item and its line total; the admin PDF
            adds QTY and Price columns (showCostColumns). */}
        {showCostColumns ? (
          <colgroup>
            <col style={{ width: "48%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "19%" }} />
          </colgroup>
        ) : (
          <colgroup>
            <col style={{ width: "78%" }} />
            <col style={{ width: "22%" }} />
          </colgroup>
        )}
        <thead>
          <tr>
            <th style={S.thLeft}>Line Item</th>
            {showCostColumns && <th style={S.thRight}>QTY</th>}
            {showCostColumns && <th style={S.thRight}>Price</th>}
            <th style={S.thRight}>Line Total</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.name}>
              <tr>
                <td style={S.catHead} colSpan={totalCols}>
                  {g.name}
                </td>
              </tr>
              {g.items.map((it, i) => (
                <tr key={i}>
                  <td style={S.tdLeft}>{it.name}</td>
                  {showCostColumns && <td style={S.tdRight}>{it.qty}</td>}
                  {showCostColumns && <td style={S.tdRight}>${fmt(it.price)}</td>}
                  <td style={S.tdRight}>${fmt(it.lineTotal)}</td>
                </tr>
              ))}
              {lastPrimaryName != null && g.name === lastPrimaryName && (
                <tr>
                  <td style={S.baseBidLabel} colSpan={labelSpan}>Base Bid</td>
                  <td style={S.baseBidValue}>${fmt(baseBid)}</td>
                </tr>
              )}
            </Fragment>
          ))}
          <tr>
            <td style={S.totalLabel} colSpan={labelSpan}>Total</td>
            <td style={S.totalValue}>${fmt(total)}</td>
          </tr>
        </tbody>
      </table>
      {/* pageBreak class matches the html2pdf pagebreak.before selector */}
      <div className="pageBreak" style={S.footer}>
        <p>
          Change Orders:&nbsp;&nbsp;&nbsp;Price includes standard floor preparation, defined as
          light skimcoating of hairline cracks or small recesses (less than
          2&quot; in diameter and 1/2&quot; in depth). Anything in excess
          is atypical and will incur additional floor prep costs.&nbsp;&nbsp;&nbsp;All
          changes/additional costs will be quoted for approval prior to
          commencement of additional work.&nbsp;&nbsp;&nbsp;Pricing does not include moving
          furniture, toilets, appliances, or modifications to any other unit
          finishes, electronics, or plumbing.&nbsp;&nbsp;&nbsp;Toilet or appliance moving
          must be requested before day of install and is limited to specific
          items.&nbsp;&nbsp;&nbsp;Warranty:&nbsp;&nbsp;Standard Interiors warrants all labor for a period
          of one year.&nbsp;&nbsp;&nbsp;Materials are warranted by the manufacturer.
        </p>
      </div>
    </div>
  );
};

export default OrderEstimatePrint;
